/* eslint-disable no-nested-ternary */
import { useCallback, useMemo, useState, createContext, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Center, Drawer, Loader, Stack } from '@mantine/core';
import PropTypes from 'prop-types';

import {
  CUBOH_MENU_PARENT_MAP,
  MENU_OBJECT_TYPES,
  TYPE_TO_READABLE_STRING_MAP,
} from 'constants/menuManagement';

import { copyItemsBetweenMenus } from 'helpers/menuManagement';
import CrossMenuDuplicateSelector from 'components/MenuManagement/CrossMenuDuplication/CrossMenuDuplicateSelector';
import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import NotificationWrapper from 'primitives/NotificationWrapper/NotificationWrapper';
import { HeadquarterContext } from 'context/HeadquarterContext';
import { useTranslation } from 'i18n';

const NS = 'menuManagementCrossMenuDuplication';

export const TargetParentSelectDrawerContext = createContext();

export default function TargetParentSelectDrawer({
  record,
  opened,
  closeTargetParentSelectDrawer,
}) {
  const { t } = useTranslation(NS);

  const { isSingleMerchantType } = useContext(HeadquarterContext);
  const targetParentItemType = CUBOH_MENU_PARENT_MAP[record?.itemType][0];
  const readableSourceItemType = TYPE_TO_READABLE_STRING_MAP[record?.itemType];

  const shouldLockTargetLocation =
    isSingleMerchantType && record?.itemType !== MENU_OBJECT_TYPES.menus;

  const nullSelections = useMemo(
    () => ({
      companyId: null,
      merchantId: null,
      [MENU_OBJECT_TYPES.menus]: null,
      [MENU_OBJECT_TYPES.categories]: null,
      [MENU_OBJECT_TYPES.items]: null,
      [MENU_OBJECT_TYPES.modifierGroups]: null,
    }),
    [],
  );
  const defaultSelectItemData = {
    companyId: {
      title: t('TargetParentSelectDrawer.targetLocationTitle'),
      placeholder: t('TargetParentSelectDrawer.selectLocationPlaceholder'),
      noDataMessage: t('TargetParentSelectDrawer.noLocationsAvailable'),
      options: [],
    },
    merchantId: {
      title: t('TargetParentSelectDrawer.targetMerchantTitle'),
      instructionTooltip: t('TargetParentSelectDrawer.selectValidLocationFirst'),
      selectTooltip: t('TargetParentSelectDrawer.selectMerchantTooltip', {
        itemType: readableSourceItemType,
      }),
      placeholder: t('TargetParentSelectDrawer.selectMerchantPlaceholder'),
      noDataMessage: t('TargetParentSelectDrawer.noMerchantsAvailable'),
      options: [],
    },
    [MENU_OBJECT_TYPES.menus]: {
      title: t('TargetParentSelectDrawer.targetMenuTitle'),
      placeholder: t('TargetParentSelectDrawer.selectMenuPlaceholder'),
      noDataMessage: t('TargetParentSelectDrawer.noEligibleMenusFound'),
      options: [],
    },
    [MENU_OBJECT_TYPES.categories]: {
      title: t('TargetParentSelectDrawer.targetCategoryTitle'),
      placeholder: t('TargetParentSelectDrawer.selectCategoryPlaceholder'),
      noDataMessage: t('TargetParentSelectDrawer.noCategoriesFound'),
      options: [],
    },
    [MENU_OBJECT_TYPES.items]: {
      title: t('TargetParentSelectDrawer.targetMainItemTitle'),
      placeholder: t('TargetParentSelectDrawer.selectMainItemPlaceholder'),
      noDataMessage: t('TargetParentSelectDrawer.noMainItemsFound'),
      options: [],
    },
    [MENU_OBJECT_TYPES.modifierGroups]: {
      title: t('TargetParentSelectDrawer.targetModifierGroupTitle'),
      placeholder: t('TargetParentSelectDrawer.selectModifierGroupPlaceholder'),
      noDataMessage: t('TargetParentSelectDrawer.noModifierGroupsFound'),
      options: [],
    },
  };

  const [selectedOptions, setSelectedOptions] = useState(nullSelections);
  const [selectItemData, setSelectItemData] = useState(defaultSelectItemData);
  const [isInProgress, setIsInProgress] = useState(false);

  useEffect(() => {
    if (!opened) return;
    if (!shouldLockTargetLocation) return;
    if (!record?.companyId || !record?.merchantId) return;

    setSelectedOptions(prev => {
      if (prev.companyId === record.companyId && prev.merchantId === record.merchantId) {
        return prev;
      }
      return {
        ...prev,
        companyId: record.companyId,
        merchantId: record.merchantId,
      };
    });
  }, [opened, record?.companyId, record?.merchantId, shouldLockTargetLocation]);

  const handleCancel = useCallback(() => {
    setSelectedOptions(nullSelections);
    setSelectItemData(prev => ({
      ...prev,
      [MENU_OBJECT_TYPES.menus]: {
        ...prev[MENU_OBJECT_TYPES.menus],
        error: null,
      },
    }));
    closeTargetParentSelectDrawer();
  }, [closeTargetParentSelectDrawer, nullSelections]);

  const handleDuplicate = useCallback(async () => {
    try {
      setIsInProgress(true);
      await copyItemsBetweenMenus({
        targetParentRecord: {
          uuid: selectedOptions[targetParentItemType],
          companyId: record.companyId,
          merchantId: record.merchantId,
        },
        targetItemType: record.itemType,
        sourceItemList: [record?.uuid],
        duplicateChildren: true,
      });
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setSelectedOptions(nullSelections);
      setIsInProgress(false);
      closeTargetParentSelectDrawer();
    }
  }, [
    record,
    selectedOptions,
    setSelectedOptions,
    targetParentItemType,
    nullSelections,
    closeTargetParentSelectDrawer,
  ]);

  const providerValue = useMemo(
    () => ({
      selectedOptions,
      setSelectedOptions,
      selectItemData,
      setSelectItemData,
      record,
    }),
    [selectedOptions, setSelectedOptions, selectItemData, setSelectItemData, record],
  );

  const selectModalChildren = (
    <>
      {selectItemData[MENU_OBJECT_TYPES.menus]?.error && (
        <Stack mb="lg">
          <NotificationWrapper variant="caution" size="compact" mb="md">
            {selectItemData[MENU_OBJECT_TYPES.menus].error}
          </NotificationWrapper>
        </Stack>
      )}
      <CrossMenuDuplicateSelector
        selectType="companyId"
        context={TargetParentSelectDrawerContext}
        disabled={shouldLockTargetLocation}
        recordCompanyId={record?.companyId?.toString()}
      />
      <CrossMenuDuplicateSelector
        selectType="merchantId"
        context={TargetParentSelectDrawerContext}
        disabled={!selectedOptions.companyId}
        hidden={shouldLockTargetLocation}
      />
      <CrossMenuDuplicateSelector
        selectType={MENU_OBJECT_TYPES.menus}
        context={TargetParentSelectDrawerContext}
        disabled={!selectedOptions.merchantId}
      />
      <CrossMenuDuplicateSelector
        selectType={MENU_OBJECT_TYPES.categories}
        context={TargetParentSelectDrawerContext}
        disabled={!selectedOptions[MENU_OBJECT_TYPES.menus]}
        hidden={[MENU_OBJECT_TYPES.categories].includes(record.itemType)}
      />
      <CrossMenuDuplicateSelector
        selectType={MENU_OBJECT_TYPES.items}
        context={TargetParentSelectDrawerContext}
        disabled={!selectedOptions[MENU_OBJECT_TYPES.categories]}
        hidden={[MENU_OBJECT_TYPES.categories, MENU_OBJECT_TYPES.items].includes(record.itemType)}
      />
      <CrossMenuDuplicateSelector
        selectType={MENU_OBJECT_TYPES.modifierGroups}
        context={TargetParentSelectDrawerContext}
        disabled={!selectedOptions[MENU_OBJECT_TYPES.items]}
        hidden={[
          MENU_OBJECT_TYPES.categories,
          MENU_OBJECT_TYPES.items,
          MENU_OBJECT_TYPES.modifierGroups,
        ].includes(record.itemType)}
      />
    </>
  );

  return (
    <TargetParentSelectDrawerContext.Provider value={providerValue}>
      <Drawer
        title={t('TargetParentSelectDrawer.drawerTitle', { itemType: readableSourceItemType })}
        opened={opened}
        onClose={handleCancel}
        variant="withDock"
      >
        <DockedDrawerFormContent
          dockButtons={
            <Button disabled={!selectedOptions[targetParentItemType]} onClick={handleDuplicate}>
              {t('TargetParentSelectDrawer.copyButton')}
            </Button>
          }
        >
          {isInProgress ? (
            <Center mt="xl">
              <Loader />
            </Center>
          ) : (
            selectModalChildren
          )}
        </DockedDrawerFormContent>
      </Drawer>
    </TargetParentSelectDrawerContext.Provider>
  );
}

TargetParentSelectDrawer.propTypes = {
  closeTargetParentSelectDrawer: PropTypes.func.isRequired,
  opened: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object.isRequired,
};
